/**
 * Client hydration root for public SSR routes. Must match the exact tree from entry-server
 * (HelmetProvider, QueryClientProvider, Router, ConfigProvider, SSRContextProvider, Page)
 * so that server HTML and client first paint are identical and hydration succeeds.
 */
import React, { useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider } from 'antd'
import { getWebPortalUrl } from '../config/runtimeEnv'
import { SSRContextProvider } from './SSRContext'
import { getPublicPageComponent, isPublicSSRPath } from './publicRoutes'

const theme = {
  token: {
    colorPrimary: '#ea4335',
    borderRadius: 8,
    fontSize: 14
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false }
  }
})

function PublicPage(): React.ReactElement | null {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const isPublic = isPublicSSRPath(pathname)
  const ssoToken = new URLSearchParams(location.search).get('token')

  useEffect(() => {
    // Cross-app SSO: leave the public SSR shell and reload into the authenticated SPA.
    if (ssoToken) {
      try {
        localStorage.setItem('token', ssoToken)
      } catch {
        // ignore storage failures; App useAuth will still read ?token= on reload
      }
      window.location.assign(`/inbox?token=${encodeURIComponent(ssoToken)}`)
      return
    }
    if (!isPublic) {
      window.location.assign(location.pathname + location.search + location.hash)
    }
  }, [isPublic, location.pathname, location.search, location.hash, ssoToken])

  if (ssoToken || !isPublic) {
    return null
  }

  const PageComponent = getPublicPageComponent(pathname)
  return <PageComponent />
}

const PublicHydrationRoot: React.FC = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : `https://${host}`
  const webPortalUrl = typeof window !== 'undefined' ? getWebPortalUrl() : ''

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ConfigProvider theme={theme}>
            <SSRContextProvider host={host} baseUrl={baseUrl} webPortalUrl={webPortalUrl}>
              <PublicPage />
            </SSRContextProvider>
          </ConfigProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default PublicHydrationRoot
